import React, { useState, useEffect } from 'react';
import { Shield, AlertTriangle, Download, CheckCircle, RefreshCw, ChevronDown, ChevronUp, FileText, Clock, Ban, RotateCcw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function RetencionTab() {
  const token = localStorage.getItem('token');
  const authHeaders = { Authorization: `Bearer ${token}` };

  const [pendientes, setPendientes] = useState([]);
  const [historial, setHistorial] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showHistorial, setShowHistorial] = useState(false);
  const [ejecutando, setEjecutando] = useState(null);
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [mesasAdicionales, setMesasAdicionales] = useState({});
  const [buscandoMesas, setBuscandoMesas] = useState(null);

  useEffect(() => {
    // Calcular semana actual (lunes a domingo)
    const hoy = new Date();
    const dia = hoy.getDay();
    const diffLunes = dia === 0 ? -6 : 1 - dia;
    const lunes = new Date(hoy);
    lunes.setDate(hoy.getDate() + diffLunes);
    const domingo = new Date(lunes);
    domingo.setDate(lunes.getDate() + 6);
    setFechaDesde(lunes.toISOString().split('T')[0]);
    setFechaHasta(domingo.toISOString().split('T')[0]);

    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      const [pendRes, histRes] = await Promise.all([
        axios.get(`${API}/retenciones/pendientes`, { headers: authHeaders }),
        axios.get(`${API}/retenciones/historial`, { headers: authHeaders })
      ]);
      setPendientes(pendRes.data.pendientes || []);
      setHistorial(histRes.data.retenciones || []);
    } catch (err) {
      console.error('Error cargando retenciones:', err);
    }
    setLoading(false);
  };

  const ejecutarRetencion = async (par) => {
    if (!fechaDesde || !fechaHasta) {
      toast.error('Selecciona el rango de fechas del periodo de denuncia');
      return;
    }
    setEjecutando(par.ar_id + par.br_id);
    try {
      const body = {
        ar_id: par.ar_id,
        ar_nombre: par.ar_nombre,
        ar_club: par.ar_club,
        ar_club_id: par.ar_club_id || '',
        ar_union_id: par.ar_union_id || '',
        br_id: par.br_id,
        br_nombre: par.br_nombre,
        br_club: par.br_club,
        br_club_id: par.br_club_id || '',
        ganancia: Math.abs(par.ar_ganancia || 0),
        cuota: Math.abs(par.cuota || 0),
        fecha_desde: fechaDesde,
        fecha_hasta: fechaHasta,
        mesas_ids: par.mesas_ids || '-',
        mesas_adicionales: mesasAdicionales[par.ar_id + par.br_id] || []
      };
      const res = await axios.post(`${API}/retenciones/ejecutar`, body, { headers: authHeaders });
      if (res.data.success) {
        toast.success(res.data.mensaje);
        cargarDatos();
      } else {
        toast.error(res.data.mensaje || 'Error al ejecutar');
      }
    } catch (err) {
      toast.error('Error: ' + (err.response?.data?.detail || err.message));
    }
    setEjecutando(null);
  };

  const descargarPDF = async (ar_id, br_id) => {
    try {
      const res = await axios.get(`${API}/retenciones/generar-pdf/${ar_id}/${br_id}`, {
        headers: authHeaders,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Retencion_CD_${ar_id}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF descargado');
    } catch (err) {
      toast.error('Error descargando PDF');
    }
  };

  const resetRetencion = async (ar_id, br_id) => {
    if (!window.confirm('Esto eliminara la retencion para poder ejecutarla nuevamente con la fecha correcta. Continuar?')) return;
    try {
      const res = await axios.delete(`${API}/retenciones/reset/${ar_id}/${br_id}`, { headers: authHeaders });
      if (res.data.success) {
        toast.success(res.data.mensaje);
        cargarDatos();
      } else {
        toast.error(res.data.mensaje);
      }
    } catch (err) {
      toast.error('Error: ' + (err.response?.data?.detail || err.message));
    }
  };

  const buscarMesasAdicionales = async (par) => {
    if (!fechaDesde || !fechaHasta) {
      toast.error('Selecciona rango de fechas primero');
      return;
    }
    const key = par.ar_id + par.br_id;
    setBuscandoMesas(key);
    try {
      const res = await axios.get(`${API}/retenciones/mesas-adicionales/${par.ar_id}/${par.br_id}`, {
        headers: authHeaders,
        params: { fecha_desde: fechaDesde, fecha_hasta: fechaHasta }
      });
      if (res.data.success) {
        setMesasAdicionales(prev => ({ ...prev, [key]: res.data.mesas_adicionales }));
        if (res.data.total > 0) {
          toast.success(`${res.data.total} mesa(s) adicional(es) encontrada(s)`);
        } else {
          toast.info('No se encontraron mesas adicionales en el rango');
        }
      }
    } catch (err) {
      toast.error('Error buscando mesas');
    }
    setBuscandoMesas(null);
  };

  const pendientesSinRetencion = pendientes.filter(p => !p.retencion_ejecutada);
  const pendientesConRetencion = pendientes.filter(p => p.retencion_ejecutada);

  return (
    <div className="space-y-4" data-testid="retencion-tab">
      {/* Selector de periodo */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm font-bold text-gray-700 dark:text-gray-300">Periodo de denuncia:</span>
          <input
            type="date"
            value={fechaDesde}
            onChange={(e) => setFechaDesde(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200"
            data-testid="retencion-fecha-desde"
          />
          <span className="text-gray-400">a</span>
          <input
            type="date"
            value={fechaHasta}
            onChange={(e) => setFechaHasta(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200"
            data-testid="retencion-fecha-hasta"
          />
          <button
            onClick={cargarDatos}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </button>
        </div>
      </div>

      {/* Casos pendientes de retencion */}
      {pendientesSinRetencion.length > 0 && (
        <div className="bg-red-50 dark:bg-red-900/20 rounded-xl shadow-lg border border-red-200 dark:border-red-700 p-4">
          <h3 className="font-bold text-red-800 dark:text-red-300 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Pendientes de Retencion ({pendientesSinRetencion.length})
          </h3>
          <div className="space-y-3">
            {pendientesSinRetencion.map((par, idx) => (
              <div key={idx} className="bg-white dark:bg-gray-800 border border-red-200 dark:border-red-700 rounded-lg p-4 shadow-sm">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 space-y-2">
                    {/* Badge de tipo y vez */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs px-2 py-0.5 rounded font-bold bg-red-100 text-red-700">
                        Chip Dumping
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded font-bold ${
                        par.retenciones_previas === 0 ? 'bg-yellow-100 text-yellow-700' :
                        par.retenciones_previas === 1 ? 'bg-orange-100 text-orange-700' :
                        'bg-red-200 text-red-800'
                      }`}>
                        {par.retenciones_previas === 0 ? '1ra vez - 18%' :
                         par.retenciones_previas === 1 ? '2da vez - 50%' :
                         `${par.retenciones_previas + 1}a vez - BAN`}
                      </span>
                      <span className="text-sm text-gray-500">Score: {par.score}</span>
                    </div>

                    {/* BR (perdedor) y AR (ganador) */}
                    <div className="flex flex-wrap gap-3">
                      <div className="flex items-center gap-1 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 px-3 py-1.5 rounded-lg text-sm">
                        <span className="text-xs text-red-500 font-bold">BR (pierde):</span>
                        <span className="font-bold text-red-800 dark:text-red-300">{par.br_id}</span>
                        <span className="text-red-700 dark:text-red-400">{par.br_nombre}</span>
                        <span className="text-xs text-gray-500">({par.br_club})</span>
                      </div>
                      <div className="flex items-center gap-1 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 px-3 py-1.5 rounded-lg text-sm">
                        <span className="text-xs text-green-500 font-bold">AR (gana):</span>
                        <span className="font-bold text-green-800 dark:text-green-300">{par.ar_id}</span>
                        <span className="text-green-700 dark:text-green-400">{par.ar_nombre}</span>
                        <span className="text-xs text-gray-500">({par.ar_club})</span>
                      </div>
                    </div>

                    {/* Montos */}
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-gray-600 dark:text-gray-400">
                        Ganancia AR: <span className="font-bold text-green-600">{Math.abs(par.ar_ganancia || 0).toFixed(2)}</span>
                      </span>
                      <span className="text-gray-600 dark:text-gray-400">
                        Retencion sugerida: <span className="font-bold text-red-600">{par.porcentaje_sugerido}%</span>
                      </span>
                    </div>

                    {/* Advertencia */}
                    <p className="text-xs text-orange-600 dark:text-orange-400 italic">{par.advertencia}</p>

                    {/* Boton buscar mesas adicionales */}
                    <div className="flex items-center gap-2 mt-1">
                      <button
                        onClick={() => buscarMesasAdicionales(par)}
                        disabled={buscandoMesas === (par.ar_id + par.br_id)}
                        className="text-xs px-3 py-1 bg-indigo-100 hover:bg-indigo-200 text-indigo-700 rounded font-semibold transition-colors"
                      >
                        {buscandoMesas === (par.ar_id + par.br_id) ? 'Buscando...' : `Buscar mesas adicionales (${fechaDesde} a ${fechaHasta})`}
                      </button>
                    </div>

                    {/* Mesas adicionales encontradas */}
                    {mesasAdicionales[par.ar_id + par.br_id]?.length > 0 && (
                      <div className="mt-2 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-700 rounded-lg p-2">
                        <p className="text-xs font-bold text-indigo-700 dark:text-indigo-300 mb-1">
                          Mesas adicionales detectadas ({mesasAdicionales[par.ar_id + par.br_id].length}):
                        </p>
                        <div className="space-y-1">
                          {mesasAdicionales[par.ar_id + par.br_id].map((m, i) => (
                            <div key={i} className="flex items-center gap-3 text-xs text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 px-2 py-1 rounded">
                              <span className="font-mono font-bold">{m.partida_id}</span>
                              <span>{m.fecha}</span>
                              <span className="text-red-600">BR: {m.br_gan?.toFixed(2)}</span>
                              <span className="text-green-600">AR: {m.ar_gan?.toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Boton ejecutar */}
                  <button
                    onClick={() => ejecutarRetencion(par)}
                    disabled={ejecutando === (par.ar_id + par.br_id)}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-lg font-bold text-sm transition-all shadow ${
                      ejecutando === (par.ar_id + par.br_id)
                        ? 'bg-gray-400 text-white cursor-not-allowed'
                        : 'bg-red-600 hover:bg-red-700 text-white'
                    }`}
                    data-testid={`ejecutar-retencion-${par.ar_id}`}
                  >
                    {ejecutando === (par.ar_id + par.br_id) ? (
                      <><RefreshCw className="w-4 h-4 animate-spin" /> Ejecutando...</>
                    ) : (
                      <><Shield className="w-4 h-4" /> Ejecutar Retencion</>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sin pendientes */}
      {pendientesSinRetencion.length === 0 && !loading && (
        <div className="bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-700 p-6 text-center">
          <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
          <p className="text-green-700 dark:text-green-300 font-semibold">No hay retenciones pendientes</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Importa la respuesta de PPPoker en el tab Chip Dumping para ver casos confirmados</p>
        </div>
      )}

      {/* Historial de retenciones ejecutadas */}
      {historial.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-4">
          <button
            onClick={() => setShowHistorial(!showHistorial)}
            className="w-full flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg p-1 transition-colors"
          >
            <h3 className="font-bold text-gray-800 dark:text-gray-200 flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Historial de Retenciones ({historial.length})
            </h3>
            {showHistorial ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
          </button>

          {showHistorial && (
            <div className="space-y-3 mt-3">
              {historial.map((ret, idx) => (
                <div key={idx} className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-3">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-xs px-2 py-0.5 rounded font-bold ${
                          ret.numero_retencion === 1 ? 'bg-yellow-100 text-yellow-700' :
                          ret.numero_retencion === 2 ? 'bg-orange-100 text-orange-700' :
                          'bg-red-200 text-red-800'
                        }`}>
                          {ret.numero_retencion === 1 ? '1ra' : ret.numero_retencion === 2 ? '2da' : `${ret.numero_retencion}a`} Retencion - {ret.porcentaje}%
                        </span>
                        <span className="text-xs text-gray-500">{ret.fecha_ejecucion?.slice(0, 10)}</span>
                        <span className="text-xs text-gray-400">Periodo: {ret.fecha_desde} a {ret.fecha_hasta}</span>
                      </div>
                      <div className="flex items-center gap-3 text-sm">
                        <span>AR: <span className="font-bold">{ret.ar_id}</span> ({ret.ar_nombre})</span>
                        <span className="text-gray-400">|</span>
                        <span>BR: <span className="font-bold">{ret.br_id}</span> ({ret.br_nombre})</span>
                      </div>
                      <div className="text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Base: {ret.monto_base?.toFixed(2)}</span>
                        <span className="mx-2 text-gray-400">|</span>
                        <span className="font-bold text-red-600">Retencion: {ret.monto_retencion?.toFixed(2)}</span>
                        <span className="mx-2 text-gray-400">|</span>
                        <span className="text-gray-500">Club AR: {ret.ar_club}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => descargarPDF(ret.ar_id, ret.br_id)}
                      className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold transition-colors shadow"
                      data-testid={`descargar-pdf-${ret.ar_id}`}
                    >
                      <FileText className="w-4 h-4" />
                      Descargar PDF
                    </button>
                    <button
                      onClick={() => resetRetencion(ret.ar_id, ret.br_id)}
                      className="flex items-center gap-1.5 px-3 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-xs font-semibold transition-colors shadow"
                      data-testid={`reset-retencion-${ret.ar_id}`}
                      title="Resetear para corregir fecha"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Reset
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
